import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveToolThunk } from "../../../../store/redux/tool-actions";
import { toFormData, isValidUrl } from "../../../../utils/utils";
import { gradesFromMask, gradesToMask } from "../../../../utils/gradesUtils";
import { targetGroupTextType } from "../../../../enums/targetGroupType";
import toolLevelType from "../../../../enums/toolLevelType";
import Popup from "../../../ui/popup/Popup";
import HeaderPopup from "../../../ui/popup/headerPopup/HeaderPopup";
import ContentPopup from "../../../ui/popup/contentPopup/ContentPopup";
import FooterPopup from "../../../ui/popup/footerPopup.js/FooterPopup";
import AudienceField from "../../../ui/audienceField/AudienceField";
import NameField from "./toolNameField/NameField";
import UrlField from "./toolUrlField/UrlField";
import IconField from "./toolIconField/IconField";
import classes from "./ToolAddOrUpdatePopup.module.css";

const ToolAddOrUpdatePopup = ({ tool, onClose, targetGroup, sourceTypeId, toolLevel, studyGroupId = null }) => {

    const dispatch = useDispatch();
    const { texts } = useSelector((state) => state.translate);
    const [isLoading, setIsLoading] = useState(false);

    const { name = "", url = "", imageUrl: iconUrl = "", targetGroupId = null, gradeMask = 0 } = tool ?? {};
    const [form, setForm] = useState({ name, url, iconUrl, iconFile: null, targetGroupId, selectedGrades: gradesFromMask(gradeMask) });

    const setField = (patch) => {
        setForm((prev) => ({ ...prev, ...patch }));
    };

    const resolveTargetGroupId = (targetGroupId, gradeMask) => {
        if (gradeMask > 0) {
            if (!targetGroupId)
                return targetGroupTextType.Student;
            if (targetGroupId === targetGroupTextType.Teacher)
                return targetGroupTextType.Everyone;
        }
        return targetGroupId;
    };

    const handleSubmit = async () => {
        if (!form.name || !form.url) return;

        const iconField = form.iconFile ?
            { iconFile: form.iconFile } :
            form.iconUrl ?
                { iconUrl: form.iconUrl } : {};

        const body = {
            toolId: tool?.toolId,
            name: form.name,
            url: form.url,
            ...iconField,
            sourceTypeId,
            toolLevel: tool?.toolLevel ?? toolLevel,
            targetGroupId: toolLevel === toolLevelType.School ? resolveTargetGroupId(form.targetGroupId, gradesToMask(form.selectedGrades)) : targetGroup,
            gradeMask: toolLevel === toolLevelType.School ? gradesToMask(form.selectedGrades) : 0,
        };
        if (studyGroupId || tool?.studyGroupId)
            body.studyGroupId = studyGroupId || tool.studyGroupId;

        setIsLoading(true);
        try {
            const result = await dispatch(saveToolThunk(toFormData(body)));
            if (result)
                onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid =
        !!form.name.trim() &&
        isValidUrl(form.url) &&
        (targetGroup || form.targetGroupId !== null) &&
        (!!form.iconFile || !!form.iconUrl);

    const title = tool?.toolId ? texts.editTool : texts.addTool;

    return (
        <Popup close={onClose} accessibilityDescription={title} dropClass={classes.PopupDrop} dropClassBody={classes.PopupBody}>
            <HeaderPopup accessibilityDescription={title}>
                <p className={classes.HeaderTitle}>{title}</p>
            </HeaderPopup>

            <ContentPopup className={classes.Body} scrollDropClass={classes.BodyScroll}>
                {!tool?.toolId &&
                    <p className={classes.Desc}>
                        {texts.toolPopupDesc}
                        <span className={classes.DescSub}>{texts.toolPopupSubDesc}</span>
                    </p>
                }
                <NameField value={form.name} onChange={(v) => setField({ name: v })} />
                <UrlField value={form.url} onChange={(v) => setField({ url: v })} />
                {!targetGroup &&
                    <AudienceField
                        value={form.targetGroupId}
                        selectedGrades={form.selectedGrades}
                        onChange={(v) => setField({ targetGroupId: v })}
                        onGradesChange={(v) => setField({ selectedGrades: v })}
                    />
                }
                <IconField
                    value={form.iconUrl}
                    onFileChange={(file) => setField({ iconFile: file, iconUrl: "" })}
                    onUrlChange={(url) => setField({ iconUrl: url, iconFile: null })}
                />
            </ContentPopup>
            <FooterPopup
                onClose={onClose}
                next={handleSubmit}
                nextText={isLoading ? texts.saving : tool?.toolId ? texts.save : texts.add}
                disabled={isLoading || !isFormValid}
                classNameNext={classes.SaveBtn}
                classNameCancel={classes.CancelBtn}
                className={classes.Footer}
            />
        </Popup>
    );
};

export default ToolAddOrUpdatePopup;
